import React from 'react';
import {IPropsInjectedByViewerScript} from '../types/app-types';
import {IHostProps, ISantaProps} from '@wix/native-components-infra/dist/es/src/types/types';
import {BackInStockStoreProps} from '../viewerScript/stores/BackInStockStore';
import {useStyles} from '@wix/tpa-settings/react';
import {useExperiments as experimentHook} from '@wix/yoshi-flow-editor';

export const PropsContext = React.createContext<ProvidedGlobalProps>(undefined);

export interface ProvidedGlobalProps {
  globals?: GlobalProps;
}

export type GlobalProps = Pick<
  IPropsInjectedByViewerScript,
  | 'addedToCartStatus'
  | 'addToCartState'
  | 'breakdownParams'
  | 'reportBI'
  | 'closeWixModal'
  | 'cashierExpressCheckoutWidgetProps'
  | 'cashierExpressEstimatedTotal'
  | 'currentPrice'
  | 'formatPrice'
  | 'dynamicPaymentsMethodsAmount'
  | 'experiments'
  | 'handleAddToCart'
  | 'handleBuyNow'
  | 'handleOpenModal'
  | 'handleSubscribe'
  | 'handleUserInput'
  | 'handleWishlistButtonClick'
  | 'hasMultipleMedia'
  | 'hasCategoryPageInstalled'
  | 'hideNavigationUrls'
  | 'htmlTags'
  | 'infoSection'
  | 'isDesktop'
  | 'isEditorMode'
  | 'isEditorX'
  | 'isInteractive'
  | 'isMobile'
  | 'isPreviewMode'
  | 'isAllowedToCheckout'
  | 'isProductSubmitted'
  | 'isQuickView'
  | 'isViewProduct'
  | 'isRTL'
  | 'isResponsive'
  | 'isSEO'
  | 'isSSR'
  | 'isMobileOnDesktopMode'
  | 'isPreOrderState'
  | 'layoutId'
  | 'navigate'
  | 'navigateToLink'
  | 'onAppLoaded'
  | 'reportOptionDrawerLoaded'
  | 'reportProductPageLoadedPanorama'
  | 'pagePath'
  | 'pagination'
  | 'product'
  | 'productGroup'
  | 'productUrl'
  | 'productUrlBySlug'
  | 'groupedMemberUrls'
  | 'productWasAddedToWishlist'
  | 'quantityRange'
  | 'remainingItemCount'
  | 'resetAddedToCartStatus'
  | 'resetWishlistStatus'
  | 'selectedVariant'
  | 'setDynamicPaymentsMethodsAmount'
  | 'shouldFocusAddToCartButton'
  | 'shouldShowAddToCartSuccessAnimation'
  | 'hasPaymentOptionsBreakdown'
  | 'hasDiscountName'
  | 'shouldShowSubscribeButton'
  | 'shouldShowSubscriptionPlans'
  | 'shouldShowWishlistButton'
  | 'shouldShowUserInputs'
  | 'slots'
  | 'dynamicPaymentMethodsTheme'
  | 'siteUrl'
  | 'socialSharing'
  | 'subscriptionPlans'
  | 'subscriptionPlanVariantPrices'
  | 'subscriptionPlanFromPrices'
  | 'priceBreakdown'
  | 'texts'
  | 'userInputErrors'
  | 'scrollAndFocusInputTarget'
  | 'userInputs'
  | 'validateUserInputsChanges'
  | 'isUserInputsValid'
  | 'isStickyDrawerOpenInEditor'
  | 'isStickyContainerVisibleInEditor'
  | 'variantInfo'
  | 'instanceId'
  | 'wishlistActionStatus'
  | 'withModalGallery'
  | 'styles'
  | 'labelOverrides'
  | 'useExperiments'
  | 'toastErrorMessage'
  | 'suggestedCurrencyAutoSwitchToastIntent'
  | 'isCombinedListing'
  | keyof BackInStockStoreProps
> &
  Pick<IHostProps, 'updateLayout' | 'dimensions' | 'style' | 'accessibilityEnabled' | 'LazySentry'> & {
    host: IHostProps;
  };

export function withGlobalPropsProvider(Component) {
  // eslint-disable-next-line react/display-name
  return (props: IPropsInjectedByViewerScript & ISantaProps & {style: IHostProps['style']}) => {
    const {
      addToCartState,
      addedToCartStatus,
      breakdownParams,
      reportBI,
      cashierExpressCheckoutWidgetProps,
      cashierExpressEstimatedTotal,
      closeWixModal,
      currentPrice,
      formatPrice,
      dynamicPaymentsMethodsAmount,
      experiments,
      handleAddToCart,
      handleBuyNow,
      handleNotifyWhenBackInStock,
      handleOpenModal,
      handleSubscribe,
      handleUserInput,
      handleWishlistButtonClick,
      hasMultipleMedia,
      hasCategoryPageInstalled,
      hideNavigationUrls,
      htmlTags,
      infoSection,
      isBackInStockEnabled,
      isBackInStockLoading,
      isDesktop,
      isEditorMode,
      isEditorX,
      isInteractive,
      isMobile,
      isPreviewMode,
      isAllowedToCheckout,
      isProductSubmitted,
      isQuickView,
      isViewProduct,
      isRTL,
      isResponsive,
      isSEO,
      isSSR,
      isMobileOnDesktopMode,
      isPreOrderState,
      layoutId,
      navigate,
      navigateToLink,
      dynamicPaymentMethodsTheme,
      onAppLoaded,
      reportOptionDrawerLoaded,
      reportProductPageLoadedPanorama,
      pagePath,
      pagination,
      product,
      productGroup,
      isCombinedListing,
      productUrl,
      productUrlBySlug,
      groupedMemberUrls,
      productWasAddedToWishlist,
      quantityRange,
      remainingItemCount,
      resetAddedToCartStatus,
      resetWishlistStatus,
      selectedVariant,
      setDynamicPaymentsMethodsAmount,
      shouldFocusAddToCartButton,
      shouldShowAddToCartSuccessAnimation,
      hasPaymentOptionsBreakdown,
      hasDiscountName,
      shouldShowSubscribeButton,
      shouldShowSubscriptionPlans,
      shouldShowWishlistButton,
      shouldShowUserInputs,
      siteUrl,
      socialSharing,
      style,
      subscriptionPlans,
      subscriptionPlanVariantPrices,
      subscriptionPlanFromPrices,
      priceBreakdown,
      texts,
      userInputErrors,
      scrollAndFocusInputTarget,
      userInputs,
      validateUserInputsChanges,
      isUserInputsValid,
      isStickyDrawerOpenInEditor,
      isStickyContainerVisibleInEditor,
      variantInfo,
      instanceId,
      wishlistActionStatus,
      withModalGallery,
      labelOverrides,
      useExperiments,
      toastErrorMessage,
      suggestedCurrencyAutoSwitchToastIntent,
      ...passThroughProps
    } = props;

    const {dimensions, updateLayout, accessibilityEnabled, LazySentry} = props.host;
    const styles = useStyles();
    const experimentsHook = experimentHook().experiments;

    const globals = {
      accessibilityEnabled,
      addToCartState,
      addedToCartStatus,
      breakdownParams,
      reportBI,
      cashierExpressCheckoutWidgetProps,
      cashierExpressEstimatedTotal,
      closeWixModal,
      currentPrice,
      formatPrice,
      dimensions,
      dynamicPaymentsMethodsAmount,
      experiments,
      handleAddToCart,
      handleBuyNow,
      handleNotifyWhenBackInStock,
      handleOpenModal,
      handleSubscribe,
      handleUserInput,
      handleWishlistButtonClick,
      hasMultipleMedia,
      hasCategoryPageInstalled,
      hideNavigationUrls,
      htmlTags,
      infoSection,
      isBackInStockEnabled,
      isBackInStockLoading,
      isDesktop,
      isEditorMode,
      isEditorX,
      isInteractive,
      isMobile,
      isPreviewMode,
      isAllowedToCheckout,
      isProductSubmitted,
      isQuickView,
      isViewProduct,
      isRTL,
      isResponsive,
      isSEO,
      isSSR,
      isMobileOnDesktopMode,
      isPreOrderState,
      layoutId,
      navigate,
      navigateToLink,
      onAppLoaded,
      reportOptionDrawerLoaded,
      reportProductPageLoadedPanorama,
      pagePath,
      pagination,
      product,
      productGroup,
      productUrl,
      productUrlBySlug,
      groupedMemberUrls,
      productWasAddedToWishlist,
      quantityRange,
      remainingItemCount,
      resetAddedToCartStatus,
      resetWishlistStatus,
      selectedVariant,
      setDynamicPaymentsMethodsAmount,
      shouldFocusAddToCartButton,
      shouldShowAddToCartSuccessAnimation,
      hasPaymentOptionsBreakdown,
      hasDiscountName,
      isCombinedListing,
      shouldShowSubscribeButton,
      shouldShowSubscriptionPlans,
      shouldShowWishlistButton,
      shouldShowUserInputs,
      dynamicPaymentMethodsTheme,
      siteUrl,
      slots: passThroughProps.slots,
      socialSharing,
      style,
      styles,
      LazySentry,
      subscriptionPlans,
      subscriptionPlanVariantPrices,
      subscriptionPlanFromPrices,
      priceBreakdown,
      texts,
      updateLayout,
      userInputErrors,
      scrollAndFocusInputTarget,
      userInputs,
      validateUserInputsChanges,
      isUserInputsValid,
      isStickyDrawerOpenInEditor,
      isStickyContainerVisibleInEditor,
      variantInfo,
      instanceId,
      wishlistActionStatus,
      withModalGallery,
      labelOverrides,
      useExperiments: experimentsHook,
      toastErrorMessage,
      suggestedCurrencyAutoSwitchToastIntent,
      host: props.host,
    };

    return (
      <PropsContext.Provider value={{globals}}>
        <Component {...passThroughProps} />
      </PropsContext.Provider>
    );
  };
}

export function withGlobalProps<T>(Component: any): React.FunctionComponent<React.PropsWithChildren<T>> | any {
  // eslint-disable-next-line react/display-name
  return (props) => {
    return <PropsContext.Consumer>{(globalProps) => <Component {...globalProps} {...props} />}</PropsContext.Consumer>;
  };
}

export function useGlobals(): GlobalProps | undefined {
  const context = React.useContext(PropsContext);
  return context?.globals;
}
